<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { ref } from 'vue';
import { vClickOutside } from '@src/shared/lib/directives/clickOutside';
import { useAppConfigStore } from '@src/shared/stores/app-config';

const store = useAppConfigStore();
const { config, selectedCurrencyCode, selectedLanguageCode } = storeToRefs(store);

const isOpen = ref(false);

function toggleDropdown() {
	isOpen.value = !isOpen.value;
}

function closeDropdown() {
	isOpen.value = false;
}
</script>

<template>
	<div v-click-outside="closeDropdown" class="relative" @keydown.esc="closeDropdown">
		<button
			type="button"
			class="hidden sm:flex items-center h-11 px-4 bg-cardBg transition-all hover:border-cardBorderHover border border-cardBorder rounded-full text-primaryText gap-2 cursor-pointer"
			aria-haspopup="true"
			:aria-expanded="isOpen"
			@click="toggleDropdown"
		>
			<div class="flex items-center">
				<span class="border-r border-cardBorder pr-2 uppercase">{{ selectedCurrencyCode }}</span>
				<span class="ml-2 uppercase">{{ selectedLanguageCode }}</span>
			</div>
			<img
				src="/src/shared/assets/icons/fill-arrow-dropdown.svg"
				class="w-2 transition-transform duration-200"
				:class="isOpen ? 'rotate-180' : 'rotate-0'"
				alt=""
			/>
		</button>

		<Transition name="dropdown">
			<div
				v-if="isOpen && config"
				class="absolute right-0 z-20 mt-2 w-60 rounded-xl border border-cardBorder bg-mainBg p-3 shadow-lg shadow-black/40"
			>
				<div class="grid grid-cols-2 gap-3">
					<div class="border-r border-r-cardBorder pr-3">
						<p class="mb-2 text-xs text-secondaryText uppercase tracking-wide">Currency</p>
						<ul class="gap-y-1 flex flex-col">
							<li v-for="currency in config.currencies" :key="currency.code">
								<button
									type="button"
									class="cursor-pointer w-full rounded-md px-2 py-1.5 text-left text-sm transition-colors duration-150 hover:bg-white/10"
									:class="
										currency.code === selectedCurrencyCode
											? 'bg-white/10 text-primaryText'
											: 'text-secondaryText'
									"
									@click="store.selectCurrency(currency.code)"
								>
									{{ currency.symbol }} {{ currency.code.toUpperCase() }}
								</button>
							</li>
						</ul>
					</div>

					<div>
						<p class="mb-2 text-xs text-secondaryText uppercase tracking-wide">Language</p>
						<ul class="gap-y-1 flex flex-col">
							<li v-for="language in config.languages" :key="language.code">
								<button
									type="button"
									class="cursor-pointer w-full rounded-md px-2 py-1.5 text-left text-sm transition duration-150 hover:bg-white/10"
									:class="
										language.code === selectedLanguageCode
											? 'bg-white/10 text-primaryText'
											: 'text-secondaryText'
									"
									@click="store.selectLanguage(language.code)"
								>
									{{ language.name }}
								</button>
							</li>
						</ul>
					</div>
				</div>
			</div>
		</Transition>
	</div>
</template>

<style scoped>
.dropdown-enter-active,
.dropdown-leave-active {
	transition:
		opacity 0.15s ease,
		transform 0.15s ease;
}

.dropdown-enter-from,
.dropdown-leave-to {
	opacity: 0;
	transform: translateY(-4px);
}
</style>
